import { useEffect, useState } from "react";
import { Box, Button, Collapse, Grid, Hidden } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CustomSelectArrow } from "../../../../assets/icons/custom-svg-components/CompanySvgCollection";
import { EventFilterData } from "../../../../models/eventData";
import { AppState } from "../../../../redux";
import { getTargetGroups } from "../../../../redux/target-groups/actions";
import CustomSearchField from "../../../forms/CustomSearchField";
import EventFilterField from "./EventFilterField";
import { getCourseTopics } from "../../../../redux/courseTopics/actions";
import { getCourseSubtitles } from "../../../../redux/courseSubtitles/actions";
import { getCourseLanguages } from "../../../../redux/courseLanguage/actions";
import EventSortingField from "./EventSortingField";
import Loader from "../../../theming/loader/Loader";
import { useLoadOnce } from "../../../../hooks/useLoadOnce";
import { ParticipationType } from "../../../../models/targetGroupData";
import { Utils } from "../../../../services/utils";

/**
 * EventFilterForm
 *
 * A responsive form component that displays filters for searching and sorting events.
 * - Includes keyword search, language and thematic filters, and optional sorting
 * - Loads metadata and filter options from the backend
 * - Responsive layout with collapsible sections on small screens
 */
const EventFilterForm: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "enums", "events", "snackbars"]);
  const theme = useTheme();
  const { values } = useFormikContext<EventFilterData>();

  const {
    user: { currentUser: userData },
    targetGroups,
    courseLanguages,
    courseSubtitles,
    courseTopics,
    metaData: { metaData },
  } = useSelector((state: AppState) => state);

  let userParticipationTypes: ParticipationType[] = [];

  if (userData) {
    userParticipationTypes = userData.participation_types.map((id) => {
      return {
        id,
        document_type: "",
        label: Utils.mapTargetGroupIdToString(
          id,
          targetGroups.targetGroups.participation_types
        ),
        is_default: false,
      };
    });
  }

  const allMembers = targetGroups.targetGroups.participation_types.find(
    (x) => x.is_default
  );

  const defaultTargetGroupOptions: string[] = [];
  // TODO: Replace the label check with `is_default === true` once
  // the flag is reliable.
  const hasTargetGroupFilters =
    userParticipationTypes.length > 0 &&
    !(
      userParticipationTypes.length === 1 &&
      userParticipationTypes[0].label === "Alle Teilnehmer"
    );

  const mdDown = useMediaQuery(theme.breakpoints.down("sm"));
  const marginBottom = "1.25rem";

  const [thematicFiltersEnabled, setThematicFiltersEnabled] = useState(false);

  const [expanded, setExpanded] = useState(true);

  /**
   * Loads target groups from API if not already loaded
   */
  useEffect(() => {
    if (
      !targetGroups.isLoading &&
      !targetGroups.targetGroupsLoaded &&
      !targetGroups.isError
    ) {
      dispatch(getTargetGroups());
    }
  }, [targetGroups]);

  // Load core filter data once
  useLoadOnce(
    courseLanguages.courseLanguagesLoaded,
    courseLanguages.isLoading,
    getCourseLanguages
  );
  useLoadOnce(
    courseSubtitles.courseSubtitlesLoaded,
    courseSubtitles.isLoading,
    getCourseSubtitles
  );
  useLoadOnce(courseTopics.courseTopicsLoaded, courseTopics.isLoading, getCourseTopics);

  // Show loader while required filter data is loading
  if (
    targetGroups.isLoading ||
    courseTopics.isLoading ||
    courseLanguages.isLoading ||
    courseSubtitles.isLoading
  ) {
    return <Loader />;
  }

  return (
    <>
      <Collapse in={expanded} collapsedSize={0}>
        <Box py={mdDown ? 2 : 0}>
          <Grid container spacing={mdDown ? 1 : 2}>
            <Grid item xs={12} sm={12} style={{ marginBottom: marginBottom }}>
              <CustomSearchField
                name="search"
                id="search"
                label={t("eventFilter.keyword", { ns: "events" })}
                initialValue={values.search}
              />
            </Grid>
            {courseLanguages.courseLanguages.length > 1 ? (
              <Grid item xs={12} sm={6} md={4} style={{ marginBottom: "0.2rem" }}>
                <EventFilterField
                  allOptions={courseLanguages.courseLanguages}
                  selectedOptions={[]}
                />
              </Grid>
            ) : null}
            {courseTopics.courseTopics.length > 1 ? (
              <Grid item xs={12} sm={6} md={4} style={{ marginBottom: marginBottom }}>
                <EventFilterField
                  isThematicFilter
                  allOptions={courseTopics.courseTopics}
                  selectedOptions={[]}
                  setThematicFiltersEnabled={setThematicFiltersEnabled}
                />
              </Grid>
            ) : null}
            {hasTargetGroupFilters ? (
              <Grid item xs={12} sm={6} md={4} style={{ marginBottom: marginBottom }}>
                <EventFilterField
                  isTargetGroupFilter
                  allOptions={userParticipationTypes}
                  selectedOptions={defaultTargetGroupOptions}
                  allMembers={allMembers}
                />
              </Grid>
            ) : null}
          </Grid>
        </Box>

        {metaData?.sort_filter ? (
          <Grid container spacing={mdDown ? 1 : 2}>
            <Grid item xs={12} sm={6} md={4} style={{ marginBottom: marginBottom }}>
              <EventSortingField thematicFiltersEnabled={thematicFiltersEnabled} />
            </Grid>
          </Grid>
        ) : null}
      </Collapse>

      <Hidden>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Button
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <Box m={1} display="flex" justifyContent="center">
                <Box px="0.06rem">
                  {" "}
                  {t("eventFilter.hideOptions", { ns: "events" })}{" "}
                </Box>
                <CustomSelectArrow className="event-item-shrink-icon" />
              </Box>
            ) : (
              <Box m={1} display="flex" justifyContent="center">
                <Box px="0.06rem">
                  {" "}
                  {t("eventFilter.showOptions", { ns: "events" })}{" "}
                </Box>
                <CustomSelectArrow className="event-item-expand-icon" />
              </Box>
            )}
          </Button>
        </Box>
      </Hidden>
    </>
  );
};

export default EventFilterForm;
